import {
  Button,
  Checkbox,
  Dialog,
  LoadingIndicator,
  Plus,
  useSpacing,
} from "@surya-digital/leo-reactjs-material-ui";
import { observer } from "mobx-react";
import React, { useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useAssignEmailCycleStore,
  useEmailCycleNamesStore,
} from "../../stores/Hooks";
import { useRouteNavigator } from "../../../../routes/RoutesHelper";
import { Stack } from "@mui/material";
import { UnexpectedErrorDialog } from "../../../../components/dialogs/UnexpectedErrorDialog";
import { AssignEmailCycleRPC } from "@pulse/pulse-rpcs/lib/email-cycle/assignEmailCycleRPC";
import { ErrorDialog } from "../../../../components/dialogs/ErrorDialog";
import { getCheckBoxState } from "@pulse/shared-components";

interface ChooseEmailCycleDialogProps {
  onAssignmentSuccess: (emailCyclesCount: number) => void;
  handleOpenCreateEmailCycleDrawer: VoidFunction;
  getEmailCycleNames: VoidFunction;
  closeDialog: VoidFunction;
}

export const ChooseEmailCycleDialog: React.FC<ChooseEmailCycleDialogProps> =
  observer(
    ({
      onAssignmentSuccess,
      handleOpenCreateEmailCycleDrawer,
      getEmailCycleNames,
      closeDialog,
    }) => {
      const [
        openAssignEmailCycleUnexpectedErrorDialog,
        setOpenAssignEmailCycleUnexpectedErrorDialog,
      ] = useState(false);

      const { t } = useTranslation();
      const spacing = useSpacing();
      const navigateTo = useRouteNavigator();

      const emailCycleNamesStore = useEmailCycleNamesStore();
      const assignEmailCycleStore = useAssignEmailCycleStore();

      useLayoutEffect(() => {
        assignEmailCycleStore.clearScheduleIds();
      }, [assignEmailCycleStore]);

      const onPrimaryButtonClick = async (): Promise<void> => {
        assignEmailCycleStore
          .assignEmailCycle()
          .then((isAssignmentSuccessful) => {
            if (isAssignmentSuccessful) {
              onAssignmentSuccess(assignEmailCycleStore.scheduleIds.length);
            }
          })
          .catch(() => setOpenAssignEmailCycleUnexpectedErrorDialog(true));
      };

      const handleRefetchEmailCycleNames = (): void => {
        assignEmailCycleStore.resetRpcError();
        assignEmailCycleStore.clearScheduleIds();
        getEmailCycleNames();
      };

      if (assignEmailCycleStore.loadingAssignEmailCycle) {
        return <LoadingIndicator variant="overlay" isLoading />;
      }

      if (openAssignEmailCycleUnexpectedErrorDialog) {
        return (
          <UnexpectedErrorDialog
            message={t("emailCycles.assignEmailCycleUnexpectedErrorMessage")}
            onClickDone={closeDialog}
          />
        );
      }

      // Handling RPC Errors
      if (assignEmailCycleStore.assignEmailCycleRPCError) {
        switch (assignEmailCycleStore.assignEmailCycleRPCError) {
          case AssignEmailCycleRPC.RPCError.InvalidProjectId:
          case AssignEmailCycleRPC.RPCError.InvalidScheduleId:
            navigateTo.pageNotFound();
            break;
          case AssignEmailCycleRPC.RPCError.ProjectAlreadyArchived:
            return (
              <ErrorDialog
                title={t("common.projectAlreadyArchived")}
                message={t("common.projectAlreadyArchivedErrorText")}
                onClickClose={closeDialog}
              />
            );
          case AssignEmailCycleRPC.RPCError.EmailCycleInProgress:
            return (
              <ErrorDialog
                title={t("emailCycles.emailCycleInProgressErrorTitle")}
                message={t("emailCycles.emailCycleInProgressErrorMessage")}
                onClickClose={handleRefetchEmailCycleNames}
              />
            );
          case AssignEmailCycleRPC.RPCError.EmailCycleInactive:
            return (
              <ErrorDialog
                title={t("emailCycles.emailCycleExpiredErrorTitle")}
                message={t("emailCycles.emailCycleExpiredErrorMessage")}
                onClickClose={handleRefetchEmailCycleNames}
              />
            );
          default:
            assignEmailCycleStore.assignEmailCycleRPCError satisfies never;
        }
        return null;
      }

      // Choose Email Cycle Dialog
      return (
        <Dialog
          open={true}
          title={t("emailCycles.chooseEmailCycle")}
          primaryButtonText={t("emailCycles.assignEmailCycle")}
          isPrimaryButtonDisabled={!assignEmailCycleStore.scheduleIds.length}
          onPrimaryButtonClick={onPrimaryButtonClick}
          secondaryButtonText={t("common.close")}
          onSecondaryButtonClick={closeDialog}
          onClose={assignEmailCycleStore.resetStore}
          disableBackdropClick={true}
        >
          <Stack gap={spacing.spaceLG}>
            <Button
              label={t("emailCycles.createNewCycle")}
              name="createNewCycleButton"
              variant="outlined-color"
              size="medium"
              icon={<Plus />}
              onClick={handleOpenCreateEmailCycleDrawer}
            />
            <Stack gap={spacing.spaceSM}>
              {emailCycleNamesStore.emailCycleNames.map(
                ({ scheduleId, name }) => (
                  <Checkbox
                    key={scheduleId}
                    label={name.name}
                    state={getCheckBoxState(
                      assignEmailCycleStore.scheduleIds.includes(scheduleId),
                    )}
                    onChange={() =>
                      assignEmailCycleStore.toggleScheduleId(scheduleId)
                    }
                  />
                ),
              )}
            </Stack>
          </Stack>
        </Dialog>
      );
    },
  );
